import { useTranslation } from "react-i18next";
import { Image, Camera, Upload, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useEmitWorkspaceLogoUpdate } from "@/features/Workspaces/events/useWorkspaceLogoEvent";
import { PageLoadingSpinner } from "@/shared/components/ui/page-loading-spinner";

interface WorkspaceLogoUploadProps {
  workspaceId?: string;
  organizationId?: string;
  currentLogoUrl: string | null;
  onLogoUpdate: (url: string, file?: File) => void;
}

export const WorkspaceLogoUpload = ({
  workspaceId,
  organizationId: _organizationId,
  currentLogoUrl,
  onLogoUpdate,
}: WorkspaceLogoUploadProps): JSX.Element => {
  const { t } = useTranslation();
  const { emitWorkspaceLogoUpdate } = useEmitWorkspaceLogoUpdate();
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogoUrl);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isUploading, _setIsUploading] = useState(false);

  // Atualizar o previewUrl sempre que currentLogoUrl mudar
  useEffect(() => {
    if (currentLogoUrl) {
      setPreviewUrl(currentLogoUrl);
      setImageError(false);
    }
  }, [currentLogoUrl]);

  // Determinar se estamos lidando com um novo produto
  const isNewProduct = !workspaceId;

  // Log para debug
  useEffect(() => {
    console.log('[WorkspaceLogoUpload] Estado atual:', { 
      previewUrl, 
      isPreviewLoading,
      isNewProduct,
      currentLogoUrl,
      imageError
    });
  }, [previewUrl, isPreviewLoading, isNewProduct, currentLogoUrl, imageError]);

  // Função para adicionar timestamp à URL para evitar cache
  const getUrlWithTimestamp = (url: string): string => {
    if (!url) return '';
    return url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;
  };

  // Verificar se a URL é uma object URL (blob:)
  const isObjectUrl = (url: string): boolean => {
    return url?.startsWith('blob:') || url?.startsWith('data:');
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      // Limpar o input para permitir selecionar o mesmo arquivo novamente
      event.target.value = '';
      
      setIsPreviewLoading(true);
      setImageError(false);
      
      // Usar FileReader para criar uma URL de dados (data URL) para preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        console.log('[WorkspaceLogoUpload] Data URL criada (primeiros 50 caracteres):', dataUrl.substring(0, 50) + '...');
        
        // Atualizar o estado para exibir a imagem
        setPreviewUrl(dataUrl);
        setIsPreviewLoading(false);
        
        // Notificar o componente pai sobre o arquivo selecionado e a URL de dados
        onLogoUpdate(dataUrl, file);
        
        // Emitir evento de atualização de logo
        if (isNewProduct) {
          // Para novos produtos, usamos um ID temporário
          emitWorkspaceLogoUpdate('new-workspace-temp', dataUrl);
        } else if (workspaceId) {
          // Para produtos existentes
          emitWorkspaceLogoUpdate(workspaceId, dataUrl);
        }
      };
      
      // Iniciar a leitura do arquivo como uma URL de dados
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('[WorkspaceLogoUpload] Erro ao processar o arquivo:', error);
      setImageError(true);
      setIsPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-2 w-1/4">
      <label className="text-sm font-medium">
        {t("settings.workspaces.fields.logo")}
      </label>
      <div className="relative group w-full h-12 rounded-lg overflow-hidden">
        <label 
          htmlFor="workspace-logo-upload" 
          className="block w-full h-full cursor-pointer bg-muted"
        >
          {previewUrl && !imageError ? (
            <img
              key={`img-${previewUrl}`} /* Adicionar key para forçar re-renderização */
              src={isObjectUrl(previewUrl) ? previewUrl : getUrlWithTimestamp(previewUrl)}
              alt={t("settings.workspaces.fields.logo")}
              className="w-full h-full object-contain"
              onLoad={() => setImageError(false)}
              onError={() => setImageError(true)}
            />
          ) : imageError ? (
            <div className="flex flex-col items-center justify-center w-full h-full text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">{t('common.error')}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-full">
              <Image className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-4 h-4 text-white" />
          </div>
          {isPreviewLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Upload className="w-4 h-4 text-white animate-bounce" />
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <PageLoadingSpinner size="sm" showText={false} className="text-white" />
            </div>
          )}
        </label>
        <input
          id="workspace-logo-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleLogoUpload}
          disabled={isUploading || isPreviewLoading}
        />
      </div>
    </div>
  );
};
